import { type Pool } from '@cetusprotocol/cetus-sui-clmm-sdk';
import DepositAmount from '@components/deposit-amount';
import YourDeposited from '@components/your-deposited';
import { useComputeSwapAggregator } from '@hooks/use-compute-swap-aggregator';
import { type CoinStruct } from '@models/coins.vault.model';
import { type MyVaultsStruct } from '@models/myvault.model';
import { scaleDown } from '@utils/math';
import { Segmented } from 'antd';
import { FC, useState } from 'react';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import styles from './index.module.scss';
import DepositSubmit from './submit';

interface OwnProps {
  pool: Pool | undefined;
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  coinA: CoinStruct;
  coinB: CoinStruct;
  closeDialog(): void;
  myVault: MyVaultsStruct | undefined;
}

const WithdrawVault: FC<OwnProps> = ({ vault, coinA, coinB, pool, closeDialog, myVault }) => {
  const [tab, setTab] = useState(3);

  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [exactIn, setExactIn] = useState(true);

  const vaultTabs = [
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 3 ? styles.tab_type_active : ''}`}>
          <img src={coinB?.icon || ''} />
          <span>{coinB?.symbol || ''}</span>
        </div>
      ),
      value: 3,
    },
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 2 ? styles.tab_type_active : ''}`}>
          <img src={coinA?.icon || ''} />
          <span>{coinA?.symbol || ''}</span>
        </div>
      ),
      value: 2,
    },
    {
      label: (
        <div className={`${styles.tab_type} ${tab === 1 ? styles.tab_type_active : ''}`}>
          <img src={coinA?.icon || ''} />
          <span>+</span>
          <img src={coinB?.icon || ''} />
        </div>
      ),
      value: 1,
    },
  ];

  const handleTab = (value: number) => {
    setTab(value);
    setAmountB('');
    setAmountA('');
  };

  const isOnlyCoinA = tab === 2; // only deposit tokenA
  const isOnlyCoinB = tab === 3; // only deposit tokenB

  const { aggregatorRoute } = useComputeSwapAggregator({
    amount: isOnlyCoinA ? amountA : isOnlyCoinB ? amountB : '',
    tokenInType: isOnlyCoinA ? coinA.coin_type : coinB.coin_type,
    tokenOutType: isOnlyCoinA ? coinB.coin_type : coinA.coin_type,
    sqrt_price: pool?.current_sqrt_price.toString(),
    a2b: isOnlyCoinA ? true : false,
    lowerIndex: vault.tick_lower,
    upperIndex: vault.tick_upper,
  });

  const amount_a = scaleDown(!amountA ? 0 : amountA, coinA.decimals).toString();
  const amount_b = scaleDown(!amountB ? 0 : amountB, coinB.decimals).toString();

  return (
    <div>
      <YourDeposited myVault={myVault} vault={vault} pool={pool} coinA={coinA} coinB={coinB} />

      <div className={styles.vault_tabs}>
        <Segmented value={tab} options={vaultTabs} onChange={handleTab} />
      </div>

      <DepositAmount
        vault={vault}
        pool={pool}
        amountA={amountA}
        amountB={amountB}
        onChangeAmountA={setAmountA}
        onChangeAmountB={setAmountB}
        coinA={coinA}
        coinB={coinB}
        tabValue={tab}
        exactIn={exactIn}
        onChangeExactIn={setExactIn}
      />

      <DepositSubmit
        myVault={myVault}
        vault={vault}
        amountA={amountA}
        amountB={amountB}
        tabValue={tab}
        coinA={coinA}
        coinB={coinB}
        closeDialog={closeDialog}
        exactIn={exactIn}
        aggregatorRoute={aggregatorRoute}
      />

      {aggregatorRoute && (
        <div className={styles.vault_swap_info}>
          By depositing {isOnlyCoinA ? amount_a : amount_b}{' '}
          {isOnlyCoinA ? coinA.symbol : coinB.symbol}, {aggregatorRoute.swapAmount}{' '}
          {isOnlyCoinA ? coinA.symbol : coinB.symbol} will be converted to{' '}
          {aggregatorRoute.returnAmount} {isOnlyCoinA ? coinB.symbol : coinA.symbol} and added to
          the pool together.{' '}
          {/* {aggregatorRoute.priceImpact !== null && Number(aggregatorRoute.priceImpact) >= 0 && (
            <>
              Price impact:{' '}
              {aggregatorRoute.priceImpact > 0.0001
                ? new Decimal(aggregatorRoute.priceImpact).mul(100).toFixed(2) + '%'
                : '<0.01%'}
            </>
          )} */}
        </div>
      )}
    </div>
  );
};

export default WithdrawVault;
